import { useState } from 'react';
import type { ConfirmInvoiceInput } from '@fleet/shared';
import { parseEuro } from '@fleet/shared';
import { Button, Input } from '../../design-system';
import { Modal } from '../../components/ui/Modal';

/**
 * Popup: add a maintenance service by hand — the same thing as uploading an
 * onderhoudsfactuur, minus the document. It is persisted as a MANUAL ONDERHOUD
 * invoice with one cost line, so it shows up in the maintenance log, counts
 * towards the costs, and dates the car's `laatsteOnderhoud` like any other
 * invoice. Deleting it on the Facturen page undoes all of that.
 */
export function OnderhoudToevoegen({
  kenteken,
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  kenteken: string;
  busy?: boolean;
  /** Failure reported by the caller's save, shown next to the form. */
  error?: string;
  onSubmit: (input: ConfirmInvoiceInput) => void;
  onCancel: () => void;
}) {
  const [datum, setDatum] = useState('');
  const [omschrijving, setOmschrijving] = useState('');
  const [bedrag, setBedrag] = useState('');
  const [garage, setGarage] = useState('');
  const [factuurnummer, setFactuurnummer] = useState('');
  const [fout, setFout] = useState('');

  function opslaan(e: React.FormEvent) {
    e.preventDefault();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datum)) {
      setFout('Vul een datum in — die bepaalt wanneer het onderhoud is gedaan.');
      return;
    }
    if (bedrag.trim() && parseEuro(bedrag) == null) {
      setFout(`"${bedrag}" is geen geldig bedrag.`);
      return;
    }
    setFout('');
    onSubmit({
      type: 'ONDERHOUD',
      source: 'MANUAL',
      factuurnummer: factuurnummer.trim() || null,
      datum,
      bestandsnaam: null,
      regels: [
        {
          kenteken,
          datum,
          maand: datum.slice(0, 7),
          categorie: 'Onderhoud',
          omschrijving: omschrijving.trim() || null,
          bedragExcl: parseEuro(bedrag) ?? 0,
          station: garage.trim() || null,
        },
      ],
    });
  }

  return (
    <Modal open onClose={onCancel} title="Onderhoud toevoegen" width={480}>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#666666', lineHeight: 1.5 }}>
        Wordt opgeslagen als handmatige onderhoudsfactuur voor{' '}
        <strong style={{ color: '#111111', fontFamily: "'Roboto Mono', monospace" }}>
          {kenteken}
        </strong>{' '}
        — dus zichtbaar in het onderhoudslog en op de facturenpagina.
      </p>

      <form onSubmit={opslaan} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Input
          label="Datum onderhoud"
          type="date"
          value={datum}
          onChange={(e) => setDatum(e.target.value)}
          autoFocus
        />
        <Input
          label="Omschrijving"
          value={omschrijving}
          placeholder="bv. Grote onderhoudsbeurt"
          onChange={(e) => setOmschrijving(e.target.value)}
        />
        <Input
          label="Bedrag excl. btw"
          value={bedrag}
          placeholder="bv. 365,16"
          onChange={(e) => setBedrag(e.target.value)}
        />
        <Input
          label="Garage"
          value={garage}
          placeholder="bv. Van der Woude"
          onChange={(e) => setGarage(e.target.value)}
        />
        <Input
          label="Factuurnummer"
          value={factuurnummer}
          placeholder="optioneel"
          onChange={(e) => setFactuurnummer(e.target.value)}
        />

        {(fout || error) && (
          <div style={{ fontSize: 13, color: '#B0543B', fontWeight: 700 }}>{fout || error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="ghost" type="button" onClick={onCancel}>
            Annuleren
          </Button>
          <Button variant="primary" type="submit" disabled={busy}>
            {busy ? 'Opslaan…' : 'Onderhoud opslaan'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
